import { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from "@/firebase/config";
import { doc, getDoc, updateDoc, arrayUnion, collection, addDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Clock, AlertTriangle, CheckCircle, Save } from 'lucide-react';
import { toast } from "@/hooks/use-toast";
import { DiagramBlock } from '@/Teacherdashboard/independent/components/DiagramBlock';

// Split text around %%DIAGRAM%%...%% markers so we can render each part correctly
const splitTextByDiagrams = (text: string) => text.split(/(%%DIAGRAM%%.*?%%)/s).filter(Boolean);

const MarkdownText = ({ children, className }: { children?: string; className?: string }) => {
    const textToProcess = typeof children === 'string' ? children : String(children || '');
    // Fix LaTeX delimiters: convert \(...\) and \[...\] to KaTeX-friendly $ and $$
    const processed = textToProcess
        .replace(/\\\(([\s\S]*?)\\\)/g, (_m, inner) => `$${inner}$`)
        .replace(/\\\[([\s\S]*?)\\\]/g, (_m, inner) => `$$${inner}$$`);

    const parts = splitTextByDiagrams(processed);

    return (
        <div className={`markdown-body ${className || ''}`}>
            {parts.map((part, idx) => {
                if (part.startsWith('%%DIAGRAM%%') && part.endsWith('%%')) {
                    try {
                        const spec = JSON.parse(part.slice(11, -2).trim());
                        return (
                            <div key={idx} className="flex justify-center my-4">
                                <div className="max-w-[400px] w-full">
                                    <DiagramBlock spec={spec} />
                                </div>
                            </div>
                        );
                    } catch {
                        return <span key={idx} className="text-xs text-red-400">[Diagram error]</span>;
                    }
                }
                return (
                    <ReactMarkdown key={idx} remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                        {part}
                    </ReactMarkdown>
                );
            })}
        </div>
    );
};

const AssessmentRunner = () => {
    const { issueId } = useParams();
    const navigate = useNavigate();
    
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [data, setData] = useState<any>(null);
    const [studentInfo, setStudentInfo] = useState<any>(null);
    const [timeLeft, setTimeLeft] = useState<number>(0); // in seconds
    const [answers, setAnswers] = useState<Record<string, any>>({});

    // Load Session & Assessment
    useEffect(() => {
        const loadSession = async () => {
            // 1. Check Session
            const sessionStr = sessionStorage.getItem(`session_${issueId}`);
            if (!sessionStr) {
                navigate(`/assessment/take/${issueId}`); // Redirect to landing if no session
                return;
            }
            const session = JSON.parse(sessionStr);
            setStudentInfo(session);

            // 2. Load Assessment Data
            if (issueId === 'simulation') {
                 const simDataStr = sessionStorage.getItem('simulation_data');
                 if (simDataStr) {
                    const assessment = JSON.parse(simDataStr);
                    setData(assessment);
                    const durationSec = (assessment.config?.durationMinutes || 30) * 60;
                    setTimeLeft(durationSec);
                 }
            } else if (issueId) {
                const docRef = doc(db, 'issued_assessments', issueId);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    const assessment = docSnap.data();
                    setData(assessment);
                    
                    // Initialize Timer based on when they Started (or just duration if simplistic)
                    // For strictness, we should use server time, but for now:
                    // Duration is in minutes.
                    // If we want to strictly enforce "Expiry", we check that too.
                    // Here we just count down the allowed duration.
                    const durationSec = (assessment.config.durationMinutes || 30) * 60;
                    setTimeLeft(durationSec);
                }
            }
            setLoading(false);
        };
        loadSession();
    }, [issueId, navigate]);

    // Timer Logic
    useEffect(() => {
        if (!loading && timeLeft > 0) {
            const timer = setInterval(() => {
                setTimeLeft(prev => {
                    if (prev <= 1) {
                        clearInterval(timer);
                        handleSubmit(true); // Auto-submit
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
            return () => clearInterval(timer);
        }
    }, [loading, timeLeft]);

    // Format Time
    const formatTime = (seconds: number) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m}:${s < 10 ? '0' : ''}${s}`;
    };

    const handleAnswerChange = (qId: string, val: any) => {
        setAnswers(prev => ({ ...prev, [qId]: val }));
    };

    const handleSubmit = async (auto = false) => {
        if (submitting) return;
        setSubmitting(true);

        try {
            const submissionData = {
                issueId,
                originalAssessmentId: data.originalAssessmentId,
                student: {
                    name: studentInfo.studentName,
                    regNo: studentInfo.regNo,
                    teacherId: data.teacherId
                },
                answers,
                startedAt: studentInfo.startTime,
                submittedAt: serverTimestamp(),
                autoSubmitted: auto,
                score: null // To be graded
            };

            if (issueId === 'simulation') {
                // Mock Submission
                await new Promise(resolve => setTimeout(resolve, 1500)); // Fake network delay
                console.log("Simulated Submission:", submissionData);
            } else {
                // Use a composite ID to prevent double entries on retry
                const docId = `${issueId}_${studentInfo.regNo}`.replace(/[^a-zA-Z0-9_-]/g, '_');
                await setDoc(doc(db, 'submissions', docId), submissionData);
            }
            
            // Clear session
            sessionStorage.removeItem(`session_${issueId}`);
            
            toast({
                title: auto ? "Time's Up!" : "Submitted Successfully",
                description: "Your responses have been recorded.",
                variant: auto ? "destructive" : "default"
            });
            
            // Simple success screen replacement
            setData({ ...data, submitted: true });
            
        } catch (error) {
            console.error("Submission error:", error);
            toast({
                title: "Error",
                description: "Failed to submit assessment. Please try again.",
                variant: "destructive"
            });
            setSubmitting(false);
        }
    };

    if (loading) return <div className="p-10 text-center">Loading Assessment...</div>;

    if (data?.submitted) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 text-center animate-in zoom-in-95">
                <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-4">
                    <CheckCircle className="w-10 h-10" />
                </div>
                <h1 className="text-3xl font-bold text-gray-900">Assessment Submitted!</h1>
                <p className="text-gray-600 max-w-md">
                    Thank you, <span className="font-bold text-gray-900">{studentInfo.studentName}</span>. 
                    Your responses for <span className="font-bold">{data.subject}</span> have been securely recorded.
                </p>
                <Button onClick={() => window.location.href = '/'} variant="outline" className="mt-6">
                    Return Home
                </Button>
            </div>
        );
    }

    return (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
            {/* Sticky Timer Bar */}
            <div className={`sticky top-14 z-20 py-3 px-4 rounded-xl shadow-sm flex justify-between items-center border transition-colors ${
                timeLeft < 60 ? 'bg-red-50 border-red-200 text-red-700 animate-pulse' : 
                'bg-blue-600 text-white border-blue-500'
            }`}>
                <div className="font-bold flex items-center gap-2">
                    <Clock className="w-5 h-5" />
                    <span className="text-xl font-mono">{formatTime(timeLeft)}</span>
                </div>
                <div className="text-xs font-medium opacity-90 uppercase tracking-widest hidden md:block">
                    {data.subject} • {studentInfo.studentName}
                </div>
                <Button 
                    size="sm" 
                    variant={timeLeft < 60 ? "destructive" : "secondary"}
                    onClick={() => handleSubmit(false)}
                    disabled={submitting}
                    className="font-bold"
                >
                    {submitting ? 'Sending...' : 'Submit Now'}
                </Button>
            </div>

            {/* Questions List */}
            <div className="space-y-6 pb-20">
                {data.questions?.map((q: any, idx: number) => (
                    <Card key={q.id || idx} className="border-gray-200 dark:border-white/10 overflow-hidden">
                        <CardContent className="p-6">
                            <div className="flex items-start gap-4 mb-4">
                                <Badge variant="outline" className="h-8 w-8 rounded-full flex items-center justify-center border-gray-300 bg-gray-50 text-gray-600 font-bold shrink-0">
                                    {idx + 1}
                                </Badge>
                                <div className="space-y-1">
                                    <MarkdownText className="font-medium text-lg leading-relaxed text-gray-900 dark:text-gray-100">
                                        {q.text}
                                    </MarkdownText>
                                    <span className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">
                                        {q.marks} Marks • {q.type}
                                    </span>
                                </div>
                            </div>

                            {/* Render Input based on Type */}
                            <div className="pl-12">
                                {q.type === 'Multiple Choice' && q.options && (!q.subQuestions || q.subQuestions.length === 0) && (
                                    <RadioGroup onValueChange={(val) => handleAnswerChange(q.id, val)} value={answers[q.id]}>
                                        {q.options.map((opt: string, i: number) => (
                                            <div key={i} className={`flex items-center space-x-3 p-4 rounded-xl border transition-all cursor-pointer ${
                                                answers[q.id] === opt 
                                                ? 'bg-blue-600/10 border-blue-500/50' 
                                                : 'bg-white/5 border-white/5 hover:bg-white/10 hover:border-white/10'
                                            }`}>
                                                <RadioGroupItem value={opt} id={`${q.id}-${i}`} className="border-white/20 text-blue-500" />
                                                <Label htmlFor={`${q.id}-${i}`} className={`flex-1 cursor-pointer font-medium ${answers[q.id] === opt ? 'text-blue-400' : 'text-gray-300'}`}>
                                                    <MarkdownText>{opt}</MarkdownText>
                                                </Label>
                                            </div>
                                        ))}
                                    </RadioGroup>
                                )}

                                {(q.type === 'Short Answer' || q.type === 'Essay') && (!q.subQuestions || q.subQuestions.length === 0) && (
                                    <Textarea 
                                        placeholder="Type your answer here..." 
                                        className="min-h-[150px] border-white/10 focus:border-blue-500/50 bg-white/5 text-gray-100 placeholder:text-gray-500 resize-y rounded-xl p-4 text-sm leading-relaxed focus:bg-white/10 transition-all font-medium"
                                        onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                                        value={answers[q.id] || ''}
                                    />
                                )}

                                {q.subQuestions && q.subQuestions.length > 0 && (
                                    <div className="space-y-4 border-l border-gray-200 dark:border-white/10 pl-4">
                                        {q.subQuestions.map((sq: any, sqIndex: number) => (
                                            <div key={sq.id || sqIndex} className="space-y-3 rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50/70 dark:bg-white/5 p-4">
                                                <div className="flex items-start gap-3">
                                                    <span className="font-bold text-sm text-gray-600 dark:text-gray-300 shrink-0">
                                                        ({String.fromCharCode(97 + sqIndex)})
                                                    </span>
                                                    <div className="flex-1 space-y-1">
                                                        <MarkdownText className="text-sm leading-relaxed text-gray-900 dark:text-gray-100">
                                                            {sq.text}
                                                        </MarkdownText>
                                                        <span className="text-[11px] text-muted-foreground font-semibold uppercase tracking-wider">
                                                            {sq.marks} Marks • {sq.type}
                                                        </span>
                                                    </div>
                                                </div>

                                                {sq.type === 'Multiple Choice' && sq.options && (
                                                    <RadioGroup onValueChange={(val) => handleAnswerChange(sq.id, val)} value={answers[sq.id]}>
                                                        {sq.options.map((opt: string, i: number) => (
                                                            <div key={i} className={`flex items-center space-x-3 p-4 rounded-xl border transition-all cursor-pointer ${
                                                                answers[sq.id] === opt
                                                                ? 'bg-blue-600/10 border-blue-500/50'
                                                                : 'bg-white/5 border-white/5 hover:bg-white/10 hover:border-white/10'
                                                            }`}>
                                                                <RadioGroupItem value={opt} id={`${sq.id}-${i}`} className="border-white/20 text-blue-500" />
                                                                <Label htmlFor={`${sq.id}-${i}`} className={`flex-1 cursor-pointer font-medium ${answers[sq.id] === opt ? 'text-blue-400' : 'text-gray-300'}`}>
                                                                    <MarkdownText>{opt}</MarkdownText>
                                                                </Label>
                                                            </div>
                                                        ))}
                                                    </RadioGroup>
                                                )}

                                                {(sq.type === 'Short Answer' || sq.type === 'Essay') && (
                                                    <Textarea
                                                        placeholder="Type your answer here..."
                                                        className="min-h-[120px] border-white/10 focus:border-blue-500/50 bg-white/5 text-gray-100 placeholder:text-gray-500 resize-y rounded-xl p-4 text-sm leading-relaxed focus:bg-white/10 transition-all font-medium"
                                                        onChange={(e) => handleAnswerChange(sq.id, e.target.value)}
                                                        value={answers[sq.id] || ''}
                                                    />
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>
        </div>
    );
};

export default AssessmentRunner;
